import { scoreLabel } from '@/lib/sport/labels';
import type { FootballMatchPageData } from '@/lib/sport/public-types';
import { breadcrumbJsonLd } from '@/lib/seo';
import { absoluteUrl, SITE_NAME, siteUrl } from '@/lib/site-url';

type ClubLike = {
    id: string;
    name: string;
    slug: string;
    shortName: string | null;
};
type TeamLike = { id: string; name: string; clubId: string };

export function resolveClubSide(
    teams: TeamLike[],
    clubs: ClubLike[],
    teamId: string,
) {
    const team = teams.find((t) => t.id === teamId);
    const club = team ? clubs.find((c) => c.id === team.clubId) : undefined;
    return {
        team,
        club,
        label: club?.shortName || club?.name || team?.name || teamId,
        slug: club?.slug,
        name: club?.name || team?.name || teamId,
    };
}

export function matchPageTitle(data: FootballMatchPageData): string {
    const home = resolveClubSide(data.teams, data.clubs, data.fixture.homeTeamId);
    const away = resolveClubSide(data.teams, data.clubs, data.fixture.awayTeamId);
    const score = scoreLabel(
        data.fixture.homeGoals,
        data.fixture.awayGoals,
        data.fixture.status,
    );
    const played =
        data.fixture.status === 'played'
        || data.fixture.status === 'awarded'
        || data.fixture.status === 'live';
    const vs = played ? score : 'נגד';
    const base = `${home.label} ${vs} ${away.label}`;
    if (data.competition?.name) return `${base} | ${data.competition.name}`;
    return base;
}

export function matchPageDescription(data: FootballMatchPageData): string {
    const home = resolveClubSide(data.teams, data.clubs, data.fixture.homeTeamId);
    const away = resolveClubSide(data.teams, data.clubs, data.fixture.awayTeamId);
    const score = scoreLabel(
        data.fixture.homeGoals,
        data.fixture.awayGoals,
        data.fixture.status,
    );
    const competition = data.competition?.name;
    const season = data.season?.label;
    const venue = data.venue?.name;
    const parts = [
        competition && season ? `${competition} ${season}` : competition,
        `${home.name} נגד ${away.name}`,
        data.fixture.status === 'played' || data.fixture.status === 'awarded'
            ? `תוצאה ${score}`
            : data.fixture.status === 'live'
              ? `משחק חי ${score}`
              : 'לפני המשחק',
        venue ? `באצטדיון ${venue}` : null,
        SITE_NAME,
    ].filter(Boolean);
    return parts.join(' - ');
}

export function buildSportsEventJsonLd(
    data: FootballMatchPageData,
): Record<string, unknown>[] {
    const home = resolveClubSide(data.teams, data.clubs, data.fixture.homeTeamId);
    const away = resolveClubSide(data.teams, data.clubs, data.fixture.awayTeamId);
    const pagePath = `/football/match/${encodeURIComponent(data.fixture.id)}`;
    const pageUrl = absoluteUrl(pagePath);
    const origin = siteUrl();
    const score = scoreLabel(
        data.fixture.homeGoals,
        data.fixture.awayGoals,
        data.fixture.status,
    );

    const eventStatus =
        data.fixture.status === 'live'
            ? 'https://schema.org/EventScheduled'
            : data.fixture.status === 'played' || data.fixture.status === 'awarded'
              ? 'https://schema.org/EventScheduled'
              : data.fixture.status === 'postponed'
                ? 'https://schema.org/EventPostponed'
                : data.fixture.status === 'abandoned'
                  ? 'https://schema.org/EventCancelled'
                  : 'https://schema.org/EventScheduled';

    const homeTeam: Record<string, unknown> = {
        '@type': 'SportsTeam',
        name: home.name,
    };
    if (home.slug) homeTeam.url = absoluteUrl(`/football/club/${encodeURIComponent(home.slug)}`);

    const awayTeam: Record<string, unknown> = {
        '@type': 'SportsTeam',
        name: away.name,
    };
    if (away.slug) awayTeam.url = absoluteUrl(`/football/club/${encodeURIComponent(away.slug)}`);

    const event: Record<string, unknown> = {
        '@context': 'https://schema.org',
        '@type': 'SportsEvent',
        '@id': `${pageUrl}#event`,
        name: `${home.label} נגד ${away.label}`,
        description: matchPageDescription(data),
        url: pageUrl,
        eventStatus,
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        sport: 'Soccer',
        homeTeam,
        awayTeam,
        competitor: [homeTeam, awayTeam],
        organizer: { '@id': `${origin}/#organization` },
        inLanguage: 'he-IL',
    };

    if (data.fixture.homeGoals != null && data.fixture.awayGoals != null) {
        event.description = `${home.label} ${score} ${away.label}`;
    }
    if (data.venue?.name) {
        event.location = {
            '@type': 'Place',
            name: data.venue.name,
        };
    }
    if (data.competition?.name) {
        event.superEvent = {
            '@type': 'SportsEvent',
            name: data.competition.name,
            ...(data.competition.slug && data.season?.slug
                ? {
                    url: absoluteUrl(
                        `/football/${encodeURIComponent(data.competition.slug)}/${encodeURIComponent(data.season.slug)}`,
                    ),
                }
                : {}),
        };
    }

    const crumbs = [
        { name: 'ראשי', path: '/' },
        { name: 'כדורגל', path: '/football' },
        ...(data.competition?.slug && data.season?.slug
            ? [{
                name: data.competition.name,
                path: `/football/${encodeURIComponent(data.competition.slug)}/${encodeURIComponent(data.season.slug)}`,
            }]
            : []),
        { name: `${home.label} נגד ${away.label}`, path: pagePath },
    ];

    return [event, breadcrumbJsonLd(crumbs)];
}
